import {
  apiSlice,
  createClientV2QueryFn,
  createClientV2MutationFn,
} from 'store/slices/api';
import { parseQueryParams } from 'store/slices/utils';
import {
  ClientV2PATCHRequest,
  ClientV2PATCHResponse,
  ListResponse,
  QueryParams,
} from 'types/api';
import {
  StockLocationAssignSchema,
  StockLocationPATCHSchema,
  StockLocationSchema,
} from 'types/clientV2/stockLocations';
import { CLIENT_V2_URLS } from 'store/slices/constants/clientV2';

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getStockLocationsClientV2: builder.query<
      ListResponse<StockLocationSchema>,
      QueryParams<StockLocationSchema>
    >({
      queryFn: createClientV2QueryFn<
        ListResponse<StockLocationSchema>,
        QueryParams<StockLocationSchema>
      >((params) =>
        CLIENT_V2_URLS.STOCK_LOCATIONS(
          parseQueryParams<StockLocationSchema>(params)
        )
      ),
      providesTags: (result: ListResponse<StockLocationSchema> | undefined) => [
        { type: 'StockLocations', id: 'LIST' },
        ...(result?.data.map((item: StockLocationSchema) => ({
          type: 'StockLocations' as const,
          id: item.id,
        })) ?? []),
      ],
    }),
    updateStockLocationClientV2: builder.mutation<
      ClientV2PATCHResponse<StockLocationSchema>,
      ClientV2PATCHRequest<StockLocationPATCHSchema>
    >({
      queryFn: createClientV2MutationFn<
        ClientV2PATCHResponse<StockLocationSchema>,
        ClientV2PATCHRequest<StockLocationPATCHSchema>
      >((payload) => ({
        url: CLIENT_V2_URLS.STOCK_LOCATIONS(),
        method: 'PATCH',
        body: payload,
      })),
      invalidatesTags: (result) => [
        ...(result
          ? result.updatedIds.map(
              (id) => ({ type: 'StockLocations', id } as const)
            )
          : []),
        { type: 'StockLocations', id: 'LIST' },
      ],
    }),
    assignStockLocations: builder.mutation<
      ClientV2PATCHResponse<StockLocationSchema>,
      { id: StockLocationSchema['id']; payload: StockLocationAssignSchema }
    >({
      queryFn: createClientV2MutationFn<
        ClientV2PATCHResponse<StockLocationSchema>,
        { id: StockLocationSchema['id']; payload: StockLocationAssignSchema }
      >(({ id, payload }) => ({
        url: CLIENT_V2_URLS.STOCK_LOCATION_ASSIGN(id),
        method: 'POST',
        body: payload,
      })),
      invalidatesTags: (result) => [
        ...(result
          ? result.updatedIds.map(
              (id) => ({ type: 'StockLocations', id } as const)
            )
          : []),
        { type: 'StockLocations', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetStockLocationsClientV2Query,
  useLazyGetStockLocationsClientV2Query,
  useUpdateStockLocationClientV2Mutation,
  useAssignStockLocationsMutation,
} = extendedApiSlice;
